import {
  allSkills,
  domains,
  getSkillDomainColor,
  normalizeSkillId,
  tasks,
  type Domain,
  type Skill,
  type SkillLevel,
} from "@/data/prooftree"
import { isE2EAuthFlowStubEnabled } from "@/services/authHelpers"
import { EqEvidenceFile, uploadProofEvidence } from "@/services/proofEvidenceStorage"
import { getSupabaseClient, isSupabaseConfigured } from "@/services/supabaseClient"

type BackendMode = "supabase" | "local"
type BackendReason = "saved" | "partial" | "missing-config" | "auth-required" | "write-failed"

export type BackendWriteResult = {
  mode: BackendMode
  persisted: boolean
  reason: BackendReason
  message: string
  id?: string
}

export type EqProofSubmission = {
  skillId: string
  skillIds?: string[]
  level: string | number
  title?: string
  reflection?: string
  evidenceKind?: "upload" | "link" | "note"
  evidenceFile?: EqEvidenceFile | null
  goalId?: string | null
  taskId?: string | null
  contactId?: string | null
  proofType?: "video" | "text" | "mixed" | "link" | "note"
  content?: string
  videoUrl?: string | null
  storagePath?: string | null
}

export type EqPassportSummary = {
  skillCount: number
  levelCount: number
  verifiedLevelCount: number
  proofCount: number
  verifiedProofCount: number
  activeTaskCount: number
  progressPercent: number
}

export type EqProofPreview = {
  id: string
  title: string
  skill: string
  skillId: string
  level: string
  status: string
  color: string
}

export type EqProofTreeProgress = {
  mode: BackendMode
  domains: Domain[]
  message: string
}

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export function isSupabaseUuid(value: string | null | undefined) {
  return Boolean(value && uuidPattern.test(value))
}

export function getSeedSkillId(appSkillId: string) {
  return normalizeSkillId(appSkillId)
}

export function getAppSkillId(seedSkillId: string) {
  return normalizeSkillId(seedSkillId)
}

export function parseCheckpointLevel(level: string | number) {
  if (typeof level === "number") {
    return Math.min(4, Math.max(1, Math.round(level)))
  }

  const match = level.match(/\d+/)
  return Math.min(4, Math.max(1, Number(match?.[0] ?? 1)))
}

function localResult(reason: BackendReason, message: string): BackendWriteResult {
  return {
    mode: "local",
    persisted: false,
    reason,
    message,
  }
}

function normalizeSeedSkillIds(skillIds: string[]) {
  return Array.from(new Set(skillIds.filter(Boolean).map(getSeedSkillId)))
}

function emptyPassportSummary(): EqPassportSummary {
  return {
    skillCount: allSkills.length,
    levelCount: allSkills.reduce((sum, skill) => sum + skill.levels.length, 0),
    verifiedLevelCount: 0,
    proofCount: 0,
    verifiedProofCount: 0,
    activeTaskCount: 0,
    progressPercent: 0,
  }
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

function asString(value: unknown) {
  return typeof value === "string" ? value : ""
}

function asNumber(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) ? value : 0
}

function toLevelNumber(value: unknown) {
  return Math.min(4, Math.max(1, Math.round(asNumber(value))))
}

function toLocalProofTreeProgress(message: string): EqProofTreeProgress {
  return {
    mode: "local",
    domains,
    message,
  }
}

function toEmptyTestProofTreeProgress(message: string): EqProofTreeProgress {
  return {
    ...buildProofTreeProgress(new Map(), new Map(), message),
    mode: "local",
  }
}

function buildProofTreeProgress(
  completedLevelsBySkill: Map<string, Set<number>>,
  activeLevelsBySkill: Map<string, Set<number>>,
  message: string,
): EqProofTreeProgress {
  const progressDomains = domains.map((domain): Domain => {
    const skills = domain.skills.map((skill): Skill => {
      const completedLevels = completedLevelsBySkill.get(skill.id) ?? new Set<number>()
      const activeLevels = activeLevelsBySkill.get(skill.id) ?? new Set<number>()
      const levelCount = skill.levels.length
      const completedLevelCount = Math.min(levelCount, completedLevels.size)

      return {
        ...skill,
        completedLevels: completedLevelCount,
        progress: levelCount > 0 ? Math.round((completedLevelCount / levelCount) * 100) : 0,
        levels: skill.levels.map((level, index): SkillLevel => {
          const levelNumber = index + 1
          const status = completedLevels.has(levelNumber)
            ? "Completed"
            : activeLevels.has(levelNumber)
              ? "In Progress"
              : "Locked"

          return { ...level, status }
        }),
      }
    })
    const totalLevels = skills.reduce((sum, skill) => sum + skill.levels.length, 0)
    const completedLevels = skills.reduce((sum, skill) => sum + skill.completedLevels, 0)

    return {
      ...domain,
      skills,
      totalLevels,
      completedLevels,
      progress: totalLevels > 0 ? Math.round((completedLevels / totalLevels) * 100) : 0,
    }
  })

  return {
    mode: "supabase",
    domains: progressDomains,
    message,
  }
}

export async function submitEqProof(input: EqProofSubmission): Promise<BackendWriteResult> {
  const checkpointLevel = parseCheckpointLevel(input.level)
  const skill = allSkills.find((candidate) => candidate.id === input.skillId)
  const client = getSupabaseClient()

  if (!client) {
    return localResult(
      "missing-config",
      "Proof kept as a local draft. Supabase is not configured for this build.",
    )
  }

  const { data: userResult, error: userError } = await client.auth.getUser()

  if (userError || !userResult.user) {
    return localResult(
      "auth-required",
      "Proof kept as a local draft. Supabase is configured, but sign-in is not connected yet.",
    )
  }

  const evidenceKind = input.evidenceKind ?? "note"
  const proofType = input.proofType ?? (evidenceKind === "upload" ? "mixed" : evidenceKind)
  const initialEvidenceRefs =
    evidenceKind === "upload" && input.evidenceFile
      ? []
      : [
          {
            kind: evidenceKind,
            label: evidenceKind === "upload" ? "Selected video proof" : "Linked proof evidence",
            source: "emo-expo-app",
          },
        ]
  const { data, error } = await client
    .from("eq_proofs")
    .insert({
      user_id: userResult.user.id,
      skill_id: getSeedSkillId(input.skillId),
      checkpoint_level: checkpointLevel,
      goal_id: isSupabaseUuid(input.goalId) ? input.goalId : null,
      task_id: isSupabaseUuid(input.taskId) ? input.taskId : null,
      contact_id: isSupabaseUuid(input.contactId) ? input.contactId : null,
      proof_type: proofType,
      status: "submitted",
      visibility: "private",
      title: input.title ?? `${skill?.title ?? "EQ skill"} proof`,
      reflection: input.reflection?.trim() ?? "",
      content: input.content?.trim() ?? input.reflection?.trim() ?? "",
      video_url: input.videoUrl ?? null,
      storage_path: input.storagePath ?? null,
      evidence_refs: initialEvidenceRefs,
      metadata: {
        appSkillId: input.skillId,
        linkedSkillIds: normalizeSeedSkillIds(input.skillIds ?? [input.skillId]),
      },
      source: "emo-expo-app",
    })
    .select("id,evidence_refs")
    .single()

  if (error) {
    return localResult(
      "write-failed",
      `Proof kept as a local draft. Supabase write failed: ${error.message}`,
    )
  }

  if (input.evidenceFile && data?.id) {
    try {
      const evidenceRef = await uploadProofEvidence({
        client,
        userId: userResult.user.id,
        proofId: data.id,
        file: input.evidenceFile,
      })

      const { error: updateError } = await client
        .from("eq_proofs")
        .update({ evidence_refs: [evidenceRef] })
        .eq("id", data.id)

      if (updateError) throw updateError
    } catch (uploadError) {
      return {
        mode: "supabase",
        persisted: true,
        reason: "partial",
        message: `Proof saved, but evidence upload failed: ${
          uploadError instanceof Error ? uploadError.message : String(uploadError)
        }`,
        id: data.id,
      }
    }
  }

  return {
    mode: "supabase",
    persisted: true,
    reason: "saved",
    message: input.evidenceFile
      ? "Proof and evidence file saved to Supabase."
      : "Proof saved to Supabase and linked to the EQ skill checkpoint.",
    id: data?.id,
  }
}

export async function createEqTask(input: {
  skillId: string
  skillIds?: string[]
  level?: string | number
  title: string
  reflection?: string
  goalId?: string | null
  contactId?: string | null
  coachActionId?: string | null
  dueOn?: string | null
  visibility?: "private" | "group" | "public"
}): Promise<BackendWriteResult> {
  const client = getSupabaseClient()

  if (!client) {
    return localResult(
      "missing-config",
      "Task kept in the local session. Supabase is not configured for this build.",
    )
  }

  const { data: userResult, error: userError } = await client.auth.getUser()

  if (userError || !userResult.user) {
    return localResult(
      "auth-required",
      "Task kept in the local session. Supabase is configured, but sign-in is not connected yet.",
    )
  }

  const { data, error } = await client
    .from("eq_tasks")
    .insert({
      user_id: userResult.user.id,
      skill_id: getSeedSkillId(input.skillId),
      checkpoint_level: input.level ? parseCheckpointLevel(input.level) : null,
      goal_id: isSupabaseUuid(input.goalId) ? input.goalId : null,
      contact_id: isSupabaseUuid(input.contactId) ? input.contactId : null,
      coach_action_id: isSupabaseUuid(input.coachActionId) ? input.coachActionId : null,
      title: input.title,
      reflection: input.reflection?.trim() ?? "",
      visibility: input.visibility ?? "private",
      status: "active",
      due_on: input.dueOn ?? null,
      source: "emo-expo-app",
    })
    .select("id")
    .single()

  if (error) {
    return localResult(
      "write-failed",
      `Task kept in the local session. Supabase write failed: ${error.message}`,
    )
  }

  const skillIds = normalizeSeedSkillIds(input.skillIds ?? [input.skillId])
  if (skillIds.length > 0 && data?.id) {
    const { error: skillError } = await client.from("eq_task_skills").insert(
      skillIds.map((skillId) => ({
        task_id: data.id,
        user_id: userResult.user.id,
        skill_id: skillId,
        source: "emo-expo-app",
      })),
    )

    if (skillError) {
      return {
        mode: "supabase",
        persisted: true,
        reason: "partial",
        message: `Task saved, but skill links failed: ${skillError.message}`,
        id: data?.id,
      }
    }
  }

  return {
    mode: "supabase",
    persisted: true,
    reason: "saved",
    message: "Task saved to Supabase and linked to EQ skills.",
    id: data?.id,
  }
}

export function getLocalPassportSummary(): EqPassportSummary {
  const levelCount = allSkills.reduce((sum, skill) => sum + skill.levels.length, 0)
  const verifiedLevelCount = allSkills.reduce((sum, skill) => sum + skill.completedLevels, 0)

  return {
    skillCount: allSkills.length,
    levelCount,
    verifiedLevelCount,
    proofCount: 24,
    verifiedProofCount: 16,
    activeTaskCount: tasks.filter((task) => task.status !== "Completed").length,
    progressPercent: Math.round((verifiedLevelCount / levelCount) * 100),
  }
}

export async function getEqPassportSummary(): Promise<EqPassportSummary> {
  const client = getSupabaseClient()

  if (!client) {
    if (isE2EAuthFlowStubEnabled()) return emptyPassportSummary()
    return getLocalPassportSummary()
  }

  const { data: userResult } = await client.auth.getUser()
  if (!userResult.user) {
    return getLocalPassportSummary()
  }

  const { data, error } = await client.rpc("get_eq_passport_summary")

  if (error || !data) {
    return emptyPassportSummary()
  }

  return data as EqPassportSummary
}

export async function getEqProofTreeProgress(): Promise<EqProofTreeProgress> {
  const client = getSupabaseClient()

  if (!client) {
    if (isE2EAuthFlowStubEnabled()) {
      return toEmptyTestProofTreeProgress(
        "Supabase is not configured for this browser-auth test user, so personal progress starts empty.",
      )
    }

    return toLocalProofTreeProgress(
      "Using local ProofTree fallback because Supabase is not configured.",
    )
  }

  const { data: userResult } = await client.auth.getUser()
  if (!userResult.user) {
    return toLocalProofTreeProgress("Sign in to load Supabase-backed ProofTree progress.")
  }

  const [proofResult, taskResult] = await Promise.all([
    client
      .from("eq_proofs")
      .select("skill_id,checkpoint_level,status")
      .eq("user_id", userResult.user.id)
      .neq("status", "archived"),
    client
      .from("eq_tasks")
      .select("skill_id,checkpoint_level,status")
      .eq("user_id", userResult.user.id)
      .neq("status", "archived"),
  ])

  if (proofResult.error || taskResult.error) {
    const message =
      proofResult.error?.message ?? taskResult.error?.message ?? "Supabase progress sync failed."
    return buildProofTreeProgress(
      new Map(),
      new Map(),
      `Supabase progress sync failed, so personal progress is hidden: ${message}`,
    )
  }

  const completedLevelsBySkill = new Map<string, Set<number>>()
  const activeLevelsBySkill = new Map<string, Set<number>>()

  const addLevel = (target: Map<string, Set<number>>, seedSkillId: unknown, level: unknown) => {
    const appSkillId = getAppSkillId(asString(seedSkillId))
    if (!allSkills.some((skill) => skill.id === appSkillId)) return

    const levelNumber = toLevelNumber(level)
    const levels = target.get(appSkillId) ?? new Set<number>()
    levels.add(levelNumber)
    target.set(appSkillId, levels)
  }

  const proofRows = Array.isArray(proofResult.data) ? proofResult.data.map(asRecord) : []
  proofRows.forEach((row) => {
    const status = asString(row.status)
    if (status === "verified") {
      addLevel(completedLevelsBySkill, row.skill_id, row.checkpoint_level)
      return
    }
    addLevel(activeLevelsBySkill, row.skill_id, row.checkpoint_level)
  })

  const taskRows = Array.isArray(taskResult.data) ? taskResult.data.map(asRecord) : []
  taskRows.forEach((row) => {
    if (asString(row.status) !== "active") return
    if (!row.checkpoint_level) return
    addLevel(activeLevelsBySkill, row.skill_id, row.checkpoint_level)
  })

  return buildProofTreeProgress(
    completedLevelsBySkill,
    activeLevelsBySkill,
    "ProofTree progress loaded from Supabase proofs and tasks.",
  )
}

export async function listEqProofPreviews(): Promise<EqProofPreview[]> {
  const client = getSupabaseClient()

  if (!client) return []

  const { data: userResult } = await client.auth.getUser()
  if (!userResult.user) return []

  const { data, error } = await client
    .from("eq_proofs")
    .select("id,title,skill_id,checkpoint_level,status,updated_at,submitted_at")
    .eq("user_id", userResult.user.id)
    .neq("status", "archived")
    .order("updated_at", { ascending: false })
    .limit(8)

  if (error || !Array.isArray(data)) return []

  return data.map((item) => {
    const row = asRecord(item)
    const skillId = getAppSkillId(asString(row.skill_id))
    const skill = allSkills.find((candidate) => candidate.id === skillId) ?? allSkills[0]

    return {
      id: asString(row.id),
      title: asString(row.title) || `${skill.title} proof`,
      skill: skill.title,
      skillId,
      level: `Level ${row.checkpoint_level || 1}`,
      status: asString(row.status) || "submitted",
      color: getSkillDomainColor(skill.id),
    }
  })
}

export function getBackendModeLabel() {
  return isSupabaseConfigured() ? "Supabase ready" : "Local fallback"
}
